import { useQuery } from "@tanstack/react-query";
import {
	GetPublicAccountInfoBySlugQuery,
	GetPublicAccountInfoBySlugQueryVariables,
} from "~auth/types/__generated/gql/graphql";
import { gqlReq } from "~utils/api/gql-req";
import { gqlReqNext } from "~utils/api/gql-req/next";
import { useWebRouter } from "~utils/next-router/use-web-router";

const query = /* GraphQL */ `
	query GetPublicAccountInfoBySlug($slug: String!) {
		getPublicAccountInfoBySlug(slug: $slug) {
			id
			companyName
			slug
			subdomain
			googleAnalyticsEnabled
			brandingConfig {
				logoUrl
				presetTheme
				primaryColor
				highlightColor
				removeBranding
				ga4MeasurementId
			}
			localisationConfig {
				localisationEnabled
				overridesByLocale
			}
		}
	}
`;

const getPublicAccountInfoBySlugQueryFn = async (
	slug: string,
	signal?: AbortSignal,
) => {
	return gqlReqNext<
		GetPublicAccountInfoBySlugQuery,
		GetPublicAccountInfoBySlugQueryVariables
	>({
		query,
		variables: { slug },
		authMode: "iam",
		signal,
	});
};

export const getPublicAccountInfoBySlugQueryKey = (slug?: string) => [
	"getPublicAccountInfoBySlug",
	slug,
];

export const prefetchPublicAccountInfoBySlug = (slug: string) =>
	gqlReq<
		GetPublicAccountInfoBySlugQuery,
		GetPublicAccountInfoBySlugQueryVariables
	>({
		query,
		variables: { slug },
		authMode: "iam",
	});

const useGetPublicAccountInfoBySlug = (slug?: string) =>
	useQuery({
		enabled: Boolean(slug),
		queryKey: getPublicAccountInfoBySlugQueryKey(slug),
		queryFn: ({ signal }) => getPublicAccountInfoBySlugQueryFn(slug!, signal),
	});

export const useGetCurrentPublicAccountInfoByCurrentSlug = () => {
	const router = useWebRouter();
	return useGetPublicAccountInfoBySlug(router._query.accountName);
};
